import { api } from './client'
import type { FunnelConversionRates, FunnelStages } from './funnels'

/**
 * F-060 — report lead generation (`LeadGenerationReportController`, `/api/reports/lead-generation`).
 * Nessun importo in nessun payload. Lo stadio "opportunità" non esiste nel modello. La regione si ricava dalla
 * provincia del lead (filtro `region`, F-131) e il portale è il fornitore (`leadProviderId`).
 */
export interface LeadGenerationFilters {
  /** "YYYY-MM-DD", inclusivo. */
  from?: string
  to?: string
  /** Codice o etichetta di `LeadSources`. */
  source?: string
  /** Campagna reale (tabella CampaignLead). */
  campaignId?: string
  /** Fornitore, solo tramite le sue campagne. */
  leadProviderId?: string
  callCenterSource?: string
  agentId?: string
  /** Subordinati dell'utente indicato. */
  teamOf?: string
  departmentId?: string
  /** Operatore/prodotto del contratto in cui il lead si è convertito. */
  operatorId?: string
  productId?: string
  /** F-131 (REQ-094-001): regione italiana; il lead ci rientra se la sua provincia è di quella regione (senza provincia: escluso). */
  region?: string
  /** F-131 (REQ-094-001): settore di interesse del lead (testo, senza maiuscole); il reparto non è valorizzato sui lead. */
  sector?: string
}

export interface LeadGenerationKpi {
  received: number
  valid: number
  duplicates: number
  notQualified: number
  noShow: number
  contested: number
  invalidNumbers: number
}

export interface SourceBreakdownRow {
  source: string
  label: string
  received: number
  valid: number
  duplicates: number
  notQualified: number
  contested: number
}

export interface CampaignBreakdownRow {
  campaignId: number | null
  name: string
  received: number
  valid: number
  duplicates: number
  notQualified: number
  contested: number
}

export interface LeadGenerationReport {
  from?: string
  to?: string
  stages: FunnelStages
  conversionRates: FunnelConversionRates
  kpi: LeadGenerationKpi
  bySource: SourceBreakdownRow[]
  byCampaign: CampaignBreakdownRow[]
  limitations: string[]
}

/** Conteggio e tasso entro una finestra; entrambi null se la coorte è più giovane della finestra. */
export interface CohortWindow {
  count: number | null
  rate: number | null
}

export interface CohortWindows {
  d7: CohortWindow
  d30: CohortWindow
  d60: CohortWindow
}

export interface CohortRow {
  start: string
  end: string
  totalLeads: number
  converted: CohortWindows
  activated: CohortWindows
}

export interface LeadCohorts {
  granularity: 'week' | 'month'
  timeZone: string
  cohorts: CohortRow[]
  note: string
}

export type DisputeGroupBy = 'source' | 'campaign' | 'provider'

export interface DisputeBucket {
  key: string
  label: string
  id: number | null
  received: number
  contested: number
  contestRate: number | null
  byReason: { reason: string; count: number }[]
  /** F-120: esito delle contestazioni. */
  pending: number
  accepted: number
  rejected: number
  /** accettate / (accettate + respinte); null se nessuna e' stata decisa. */
  acceptanceRate: number | null
}

/** F-120: riga del riepilogo per fornitore (nessun importo). `providerId` null = "Senza fornitore". */
export interface ProviderSummary {
  providerId: number | null
  key: string
  label: string
  received: number
  valid: number
  duplicates: number
  invalidNumbers: number
  contested: number
  accepted: number
  pending: number
  rejected: number
  contacted: number
  qualified: number
  appointmentsSet: number
  converted: number
  activated?: number
  rates: {
    closingRate?: number | null
    leadToActivation?: number | null
    validRate: number | null
    duplicateRate: number | null
    invalidNumberRate: number | null
    contestRate: number | null
    acceptanceRate: number | null
    contactRate: number | null
    qualificationRate: number | null
    appointmentRate: number | null
    leadToSale: number | null
  }
}

/** R3-11: riga economica per fornitore (solo per chi vede gli importi). */
export interface ProviderEconomics {
  key: string
  label: string
  spend: number
  leads: number
  practices: number
  activations: number
  expected: number
  accrued: number
  collected: number
  commissions: number
  margin: number
  roi: number | null
  costPerLead: number | null
  cac: number | null
}

/** R3-11 (flag `report.lead_completo`): fornitori + cruscotto LG. `economics*` e `dashboard.cac` mancano per chi non vede gli importi. */
export interface LeadProvidersComplete {
  from?: string
  to?: string
  items: ProviderSummary[]
  economics?: ProviderEconomics[]
  economicsTotals?: ProviderEconomics
  dashboard: {
    received: number
    cac?: number | null
    callCenter: {
      contactRate: number | null
      qualificationRate: number | null
      appointmentRate: number | null
      showRate: number | null
      closingRate: number | null
      leadToActivation: number | null
    }
  }
  limitations: string[]
}

export interface LeadProviders {
  from?: string
  to?: string
  items: ProviderSummary[]
  limitations: string[]
}

export interface LeadDisputes {
  groupBy: DisputeGroupBy
  total: DisputeBucket
  items: DisputeBucket[]
  limitations: string[]
}

export type DrilldownKpi =
  | 'received'
  | 'valid'
  | 'duplicates'
  | 'invalidNumbers'
  | 'notQualified'
  | 'contested'
  | 'contacted'
  | 'qualified'
  | 'noShow'
  | 'appointmentsSet'
  | 'appointmentsCompleted'
  | 'contracts'
  | 'activations'

export interface DrilldownLeadItem {
  id: number
  name: string
  source: string | null
  status: string
  createdAt: string
  isContested: boolean
  contestReason: string | null
}

export interface DrilldownAppointmentItem {
  id: number
  leadId: number | null
  status: string
  startAt: string
  title: string
}

export interface DrilldownContractItem {
  leadId: number
  leadName?: string
  contractId: number
  /** false: il contratto fa parte del funnel ma il chiamante non può aprirlo (ADR-008). */
  contractVisible?: boolean
  referenceCode: string | null
  status: string | null
}

interface DrilldownBase {
  kpi: DrilldownKpi
  total: number
  page: number
  pageSize: number
}

export type Drilldown =
  | (DrilldownBase & { entity: 'lead'; items: DrilldownLeadItem[] })
  | (DrilldownBase & { entity: 'appointment'; items: DrilldownAppointmentItem[] })
  | (DrilldownBase & { entity: 'contract'; items: DrilldownContractItem[] })

function qs(filters: LeadGenerationFilters, extra: Record<string, string | number | undefined> = {}): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '') p.set(k, String(v))
  }
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const leadGenerationReportApi = {
  report: (f: LeadGenerationFilters = {}) => api<LeadGenerationReport>(`/api/reports/lead-generation${qs(f)}`),
  cohorts: (f: LeadGenerationFilters, granularity: 'week' | 'month') =>
    api<LeadCohorts>(`/api/reports/lead-generation/cohorts${qs(f, { granularity })}`),
  disputes: (f: LeadGenerationFilters, groupBy: DisputeGroupBy) =>
    api<LeadDisputes>(`/api/reports/lead-generation/disputes${qs(f, { groupBy })}`),
  providers: (f: LeadGenerationFilters) =>
    api<LeadProviders>(`/api/reports/lead-generation/providers${qs(f)}`),
  providersComplete: (f: LeadGenerationFilters) =>
    api<LeadProvidersComplete>(`/api/reports/lead-generation/providers/complete${qs(f)}`),
  /** F-120: URL dell'export CSV delle contestazioni (senza dati personali). */
  disputesExportUrl: (providerId: string | number | undefined, from?: string, to?: string): string =>
    `/api/reports/lead-generation/disputes/export${qs({}, { providerId, from, to })}`,
  drilldown: (f: LeadGenerationFilters, kpi: DrilldownKpi, page = 1) =>
    api<Drilldown>(`/api/reports/lead-generation/drilldown${qs(f, { kpi, page, pageSize: 50 })}`),
}
